import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageCircle } from "lucide-react";

export default function ChatWidget({ otherUserId: otherUserIdProp, title }) {
  const { user } = useAuth();
  const [otherUserId, setOtherUserId] = useState(otherUserIdProp || null);
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      if (!otherUserId && user?.role === "patient") {
        try {
          const { data } = await api.get("/messages/clinic-contact");
          setOtherUserId(data.id);
          setOther(data);
        } catch (_) {}
      }
    })();
  }, [user, otherUserId]);

  const load = async () => {
    if (!otherUserId) return;
    try {
      const { data } = await api.get(`/messages/with/${otherUserId}`);
      setMessages(data.messages || []);
      setOther((prev) => prev || data.other);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [otherUserId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!body.trim() || !otherUserId) return;
    setSending(true);
    try {
      await api.post("/messages", { to_user_id: otherUserId, body });
      setBody("");
      await load();
    } finally { setSending(false); }
  };

  return (
    <div className="bg-card border border-border rounded-sm flex flex-col h-[480px]" data-testid="chat-widget">
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-primary" />
        <div>
          <div className="font-heading font-semibold text-sm">{title || other?.name || "Chat clínico"}</div>
          <div className="overline text-muted-foreground">{user?.role === "patient" ? "Equipe farmacêutica" : "Paciente"}</div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-auto px-5 py-4 space-y-3 bg-muted/20" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-10">
            Nenhuma mensagem ainda. Envie a primeira mensagem.
          </div>
        )}
        {messages.map((m) => {
          const mine = m.from_user_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-sm text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                <div>{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "opacity-75" : "text-muted-foreground"}`}>
                  {new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-5 py-3 border-t border-border flex items-center gap-2">
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Digite sua mensagem…"
          className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="chat-input"
        />
        <button
          onClick={send}
          disabled={sending || !body.trim()}
          className="rounded-sm bg-primary text-primary-foreground px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
